import React, {
  useEffect,
  useMemo,
} from "react";

import {
  FiArrowLeft,
  FiEdit2,
  FiFileText,
  FiPrinter,
} from "react-icons/fi";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useHistory,
  useParams,
} from "react-router-dom";

import {
  fetchQuotationById,
  quotationLoadingSelector,
  selectedQuotationSelector,
} from "../../../../../../store/billing";

import "./QuotationViewPage.css";
import useAuth from "../../../../../../hooks/useAuth";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getNestedValue = (
  object,
  path,
  fallback = ""
) => {
  if (!object || !path) {
    return fallback;
  }

  const value = String(path)
    .split(".")
    .reduce(
      (
        result,
        key
      ) =>
        result?.[key],
      object
    );

  return value === undefined ||
    value === null ||
    value === ""
    ? fallback
    : value;
};

const getFirstValue = (
  object,
  paths,
  fallback = ""
) => {
  for (const path of paths) {
    const value =
      getNestedValue(
        object,
        path,
        undefined
      );

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return value;
    }
  }

  return fallback;
};

const formatDate = (
  value
) => {
  if (!value) {
    return "-";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
};

const formatCurrency = (
  value,
  currency = "INR"
) => {
  const amount =
    Number(value || 0);

  try {
    return new Intl.NumberFormat(
      "en-IN",
      {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }
    ).format(amount);
  } catch (error) {
    return `₹${amount.toLocaleString(
      "en-IN"
    )}`;
  }
};

const formatLabel = (
  value
) => {
  if (!value) {
    return "-";
  }

  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
};

const formatAddress = (
  address
) => {
  if (!address) {
    return "-";
  }

  if (
    typeof address === "string"
  ) {
    return (
      address.trim() ||
      "-"
    );
  }

  return [
    address.address ||
      address.addressLine1 ||
      address.line1,
    address.addressLine2 ||
      address.line2,
    address.city,
    address.state,
    address.pincode ||
      address.postalCode,
    address.country,
  ]
    .filter(Boolean)
    .join(", ") || "-";
};

const getStatusClass = (
  status
) => {
  switch (
    String(status || "")
      .toUpperCase()
  ) {
    case "CONFIRMED":
      return "quotation-view-status-confirmed";

    case "CANCELLED":
      return "quotation-view-status-cancelled";

    default:
      return "quotation-view-status-draft";
  }
};

const getConversionClass = (
  status
) => {
  switch (
    String(status || "")
      .toUpperCase()
  ) {
    case "CONVERTED":
      return "quotation-view-conversion-converted";

    case "PARTIALLY_CONVERTED":
      return "quotation-view-conversion-partial";

    default:
      return "quotation-view-conversion-pending";
  }
};

/*
|--------------------------------------------------------------------------
| QUOTATION VIEW PAGE
|--------------------------------------------------------------------------
*/

const QuotationViewPage = () => {
  
  const { user } =
    useAuth();

    console.log('ddddddddddddddddddddddddddddddddddd', user)

  const dispatch =
    useDispatch();

  const history =
    useHistory();

  const { id } =
    useParams();

  const quotation =
    useSelector(
      selectedQuotationSelector
    );

  const loadingState =
    useSelector(
      quotationLoadingSelector
    ) || {};

  const loading =
    Boolean(
      loadingState.detailsLoading ||
      loadingState.fetchByIdLoading
    );

  const tenant =
    user?.tenant || {};

  const tenantAddress =
    tenant?.address || {};

  const tenantSettings =
    tenant?.settings || {};

  const tenantLegalName =
    String(
      tenant?.legalName ||
      tenant?.name ||
      "BUSINESS"
    ).toUpperCase();

  const tenantMobile =
    tenant?.mobile || "-";

  const tenantEmail =
    tenant?.email || "-";

  const tenantGstin =
    tenant?.gstNumber || "-";

  const tenantPan =
    tenant?.panNumber || "-";

  const tenantAddressText =
    formatAddress(
      tenantAddress
    );

  const tenantCurrency =
    tenantSettings?.currency ||
    "INR";

  useEffect(() => {
    if (id) {
      dispatch(
        fetchQuotationById(
          id
        )
      );
    }
  }, [
    dispatch,
    id,
  ]);

  const items =
    useMemo(() => {
      if (
        !Array.isArray(
          quotation?.items
        )
      ) {
        return [];
      }

      return quotation.items;
    }, [quotation]);

  const quotationNumber =
    getFirstValue(
      quotation,
      [
        "documentNumber",
        "quotationNumber",
        "quotationNo",
        "number",
      ],
      "-"
    );

  const documentStatus =
    getFirstValue(
      quotation,
      [
        "documentStatus",
        "quotationStatus",
        "status",
      ],
      "DRAFT"
    );

  const explicitConversionStatus =
    getFirstValue(
      quotation,
      [
        "conversionStatus",
        "conversion.status",
      ],
      ""
    );

  const conversionStatus =
    explicitConversionStatus ||
    (Array.isArray(
      quotation?.convertedTo
    ) &&
    quotation.convertedTo.length > 0
      ? "CONVERTED"
      : "NOT_CONVERTED");

  const customerName =
    getFirstValue(
      quotation,
      [
        "customerSnapshot.customerName",
        "customerSnapshot.name",
        "customer.customerName",
        "customer.name",
        "customerName",
      ],
      "Walk-in Customer"
    );

  const customerMobile =
    getFirstValue(
      quotation,
      [
        "customerSnapshot.mobile",
        "customerSnapshot.phone",
        "customer.mobile",
        "customer.phone",
        "customerMobile",
      ],
      "-"
    );

  const customerEmail =
    getFirstValue(
      quotation,
      [
        "customerSnapshot.email",
        "customer.email",
        "customerEmail",
      ],
      "-"
    );

  const customerAddress =
    getFirstValue(
      quotation,
      [
        "billingAddressSnapshot",
        "billingAddress",
        "customerSnapshot.address",
        "customer.address",
      ],
      "-"
    );

  const totals =
    quotation?.totals || {};

  const subtotal =
    getFirstValue(
      quotation,
      [
        "totals.subtotal",
        "totals.grossAmount",
        "subtotal",
        "subTotal",
        "grossAmount",
      ],
      0
    );

  const discountAmount =
    getFirstValue(
      quotation,
      [
        "totals.discountAmount",
        "totals.itemDiscountAmount",
        "totals.invoiceDiscountAmount",
        "totals.totalDiscount",
        "discountAmount",
      ],
      0
    );

  const taxableAmount =
    getFirstValue(
      quotation,
      [
        "totals.taxableAmount",
        "taxableAmount",
      ],
      0
    );

  const taxAmount =
    getFirstValue(
      quotation,
      [
        "totals.taxAmount",
        "totals.totalTax",
        "taxAmount",
      ],
      0
    );

  const grandTotal =
    getFirstValue(
      quotation,
      [
        "totals.grandTotal",
        "grandTotal",
        "totalAmount",
        "netAmount",
      ],
      0
    );

  const canEdit = [
    "DRAFT",
    "CONFIRMED",
  ].includes(
    String(
      documentStatus
    ).toUpperCase()
  );

  if (loading) {
    return (
      <div className="quotation-view-page">
        <div className="quotation-view-loading-card">
          <span className="quotation-view-spinner" />

          <p>
            Loading quotation details...
          </p>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="quotation-view-page">
        <div className="quotation-view-empty-card">
          <FiFileText />

          <h2>
            Quotation Not Found
          </h2>

          <p>
            The requested quotation could
            not be found.
          </p>

          <button
            type="button"
            onClick={() =>
              history.push(
                "/billing/quotations"
              )
            }
          >
            Back to Quotations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quotation-view-page">
      <div className="quotation-view-header">
        <div className="quotation-view-heading">
          <button
            type="button"
            className="quotation-view-back-button"
            onClick={() =>
              history.push(
                "/billing/quotations"
              )
            }
          >
            <FiArrowLeft />
          </button>

          <div className="quotation-view-heading-icon">
            <FiFileText />
          </div>

          <div>
            <h1>
              {quotationNumber}
            </h1>

            <p>
              {tenantLegalName} · View quotation details,
              customer information and
              pricing summary.
            </p>
          </div>
        </div>

        <div className="quotation-view-header-actions">
          {canEdit && (
            <button
              type="button"
              className="quotation-view-edit-button"
              onClick={() =>
                history.push(
                  `/billing/quotations/${id}/edit`
                )
              }
            >
              <FiEdit2 />

              Edit
            </button>
          )}

          <button
            type="button"
            className="quotation-view-print-button"
            onClick={() =>
              history.push(
                `/billing/quotations/${id}/print`
              )
            }
          >
            <FiPrinter />

            Print
          </button>
        </div>
      </div>

      <div className="quotation-view-status-row">
        <span
          className={`quotation-view-status ${getStatusClass(
            documentStatus
          )}`}
        >
          {formatLabel(
            documentStatus
          )}
        </span>

        <span
          className={`quotation-view-conversion ${getConversionClass(
            conversionStatus
          )}`}
        >
          {formatLabel(
            conversionStatus
          )}
        </span>
      </div>

      <div className="quotation-view-info-grid">
        <div className="quotation-view-card">
          <h2>
            Quotation Information
          </h2>

          <div className="quotation-view-detail-list">
            <div>
              <span>
                Quotation Number
              </span>

              <strong>
                {quotationNumber}
              </strong>
            </div>

            <div>
              <span>
                Quotation Date
              </span>

              <strong>
                {formatDate(
                  getFirstValue(
                    quotation,
                    [
                      "documentDate",
                      "quotationDate",
                    ]
                  )
                )}
              </strong>
            </div>

            <div>
              <span>
                Valid Until
              </span>

              <strong>
                {formatDate(
                  getFirstValue(
                    quotation,
                    [
                      "validUntil",
                      "validTill",
                      "expiryDate",
                    ]
                  )
                )}
              </strong>
            </div>

            <div>
              <span>
                Sales Channel
              </span>

              <strong>
                {formatLabel(
                  getFirstValue(
                    quotation,
                    [
                      "salesChannel",
                      "channel",
                    ],
                    "-"
                  )
                )}
              </strong>
            </div>

            <div>
              <span>
                Price Mode
              </span>

              <strong>
                {formatLabel(
                  getFirstValue(
                    quotation,
                    [
                      "priceMode",
                    ],
                    "-"
                  )
                )}
              </strong>
            </div>

            <div>
              <span>
                Created On
              </span>

              <strong>
                {formatDate(
                  quotation.createdAt
                )}
              </strong>
            </div>
          </div>
        </div>

        <div className="quotation-view-card">
          <h2>
            Business Information
          </h2>

          <div className="quotation-view-customer">
            <div className="quotation-view-customer-avatar">
              {tenantLegalName
                .charAt(0)}
            </div>

            <div>
              <strong className="quotation-view-business-name">
                {tenantLegalName}
              </strong>

              <span>
                {tenantMobile}
              </span>

              <span>
                {tenantEmail}
              </span>
            </div>
          </div>

          <div className="quotation-view-address">
            <span>
              Business Address
            </span>

            <p>
              {tenantAddressText}
            </p>
          </div>

          <div className="quotation-view-business-tax">
            <div>
              <span>
                GSTIN
              </span>

              <strong>
                {tenantGstin}
              </strong>
            </div>

            <div>
              <span>
                PAN
              </span>

              <strong>
                {tenantPan}
              </strong>
            </div>
          </div>
        </div>

        <div className="quotation-view-card quotation-view-customer-card">
          <h2>
            Customer Information
          </h2>

          <div className="quotation-view-customer">
            <div className="quotation-view-customer-avatar">
              {String(
                customerName
              )
                .charAt(0)
                .toUpperCase()}
            </div>

            <div>
              <strong>
                {customerName}
              </strong>

              <span>
                {customerMobile}
              </span>

              <span>
                {customerEmail}
              </span>
            </div>
          </div>

          <div className="quotation-view-address">
            <span>
              Billing Address
            </span>

            <p>
              {typeof customerAddress ===
              "object"
                ? [
                    customerAddress.address ||
                      customerAddress.addressLine1,
                    customerAddress.addressLine2,
                    customerAddress.city,
                    customerAddress.state,
                    customerAddress.pincode,
                    customerAddress.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "-"
                : customerAddress}
            </p>
          </div>
        </div>
      </div>

      <div className="quotation-view-card">
        <div className="quotation-view-section-header">
          <div>
            <h2>
              Products
            </h2>

            <p>
              {items.length} item
              {items.length !== 1
                ? "s"
                : ""}{" "}
              added to this quotation.
            </p>
          </div>
        </div>

        <div className="quotation-view-table-scroll">
          <table className="quotation-view-table">
            <thead>
              <tr>
                <th>
                  S.No
                </th>

                <th>
                  Product
                </th>

                <th>
                  Description
                </th>

                <th>
                  HSN
                </th>

                <th>
                  Qty
                </th>

                <th>
                  Price
                </th>

                <th>
                  Discount
                </th>

                <th>
                  Tax
                </th>

                <th>
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="quotation-view-table-empty"
                  >
                    No products available.
                  </td>
                </tr>
              ) : (
                items.map(
                  (
                    item,
                    index
                  ) => {
                    const itemName =
                      getFirstValue(
                        item,
                        [
                          "itemName",
                          "productName",
                          "productSnapshot.name",
                          "productSnapshot.productName",
                          "product.name",
                        ],
                        "-"
                      );

                    const itemDescription =
                      getFirstValue(
                        item,
                        [
                          "description",
                          "productSnapshot.description",
                        ],
                        "-"
                      );

                    const itemQuantity =
                      Number(
                        getFirstValue(
                          item,
                          [
                            "quantity",
                          ],
                          0
                        )
                      );

                    const unitPrice =
                      Number(
                        getFirstValue(
                          item,
                          [
                            "unitPrice",
                            "rate",
                            "price",
                          ],
                          0
                        )
                      );

                    const discount =
                      Number(
                        getFirstValue(
                          item,
                          [
                            "discountAmount",
                            "calculatedDiscount",
                          ],
                          0
                        )
                      );

                    const itemTax =
                      Number(
                        getFirstValue(
                          item,
                          [
                            "taxAmount",
                            "calculatedTax",
                          ],
                          0
                        )
                      );

                    const itemTotal =
                      Number(
                        getFirstValue(
                          item,
                          [
                            "totalAmount",
                            "lineTotal",
                            "grandTotal",
                          ],
                          itemQuantity *
                            unitPrice
                        )
                      );

                    return (
                      <tr
                        key={
                          item._id ||
                          index
                        }
                      >
                        <td>
                          {index + 1}
                        </td>

                        <td>
                          <strong>
                            {itemName}
                          </strong>
                        </td>

                        <td>
                          {itemDescription}
                        </td>

                        <td>
                          {getFirstValue(
                            item,
                            [
                              "hsnSacCode",
                              "hsnCode",
                              "productSnapshot.hsnSacCode",
                              "productSnapshot.hsnCode",
                            ],
                            "-"
                          )}
                        </td>

                        <td>
                          {itemQuantity}
                        </td>

                        <td>
                          {formatCurrency(
                  unitPrice,
                  tenantCurrency
                )}
                        </td>

                        <td>
                          {formatCurrency(
                  discount,
                  tenantCurrency
                )}
                        </td>

                        <td>
                          {formatCurrency(
                  itemTax,
                  tenantCurrency
                )}
                        </td>

                        <td>
                          <strong>
                            {formatCurrency(
                  itemTotal,
                  tenantCurrency
                )}
                          </strong>
                        </td>
                      </tr>
                    );
                  }
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="quotation-view-bottom-grid">
        <div className="quotation-view-card">
          <h2>
            Notes & Terms
          </h2>

          <div className="quotation-view-text-block">
            <span>
              Notes
            </span>

            <p>
              {quotation.customerNotes ||
                quotation.notes ||
                "No notes added."}
            </p>
          </div>

          <div className="quotation-view-text-block">
            <span>
              Terms and Conditions
            </span>

            <p>
              {quotation.termsAndConditions ||
                "No terms and conditions added."}
            </p>
          </div>
        </div>

        <div className="quotation-view-card quotation-view-total-card">
          <h2>
            Quotation Summary
          </h2>

          <div className="quotation-view-total-row">
            <span>
              Subtotal
            </span>

            <strong>
              {formatCurrency(
                  subtotal,
                  tenantCurrency
                )}
            </strong>
          </div>

          <div className="quotation-view-total-row quotation-view-total-discount">
            <span>
              Discount
            </span>

            <strong>
              -
              {formatCurrency(
                  discountAmount,
                  tenantCurrency
                )}
            </strong>
          </div>

          <div className="quotation-view-total-row">
            <span>
              Taxable Amount
            </span>

            <strong>
              {formatCurrency(
                  taxableAmount,
                  tenantCurrency
                )}
            </strong>
          </div>

          <div className="quotation-view-total-row">
            <span>
              Total Tax
            </span>

            <strong>
              {formatCurrency(
                  taxAmount,
                  tenantCurrency
                )}
            </strong>
          </div>

          {Number(
            totals.cgstAmount ||
              0
          ) > 0 && (
            <div className="quotation-view-total-row">
              <span>
                CGST
              </span>

              <strong>
                {formatCurrency(
                  totals.cgstAmount,
                  tenantCurrency
                )}
              </strong>
            </div>
          )}

          {Number(
            totals.sgstAmount ||
              0
          ) > 0 && (
            <div className="quotation-view-total-row">
              <span>
                SGST
              </span>

              <strong>
                {formatCurrency(
                  totals.sgstAmount,
                  tenantCurrency
                )}
              </strong>
            </div>
          )}

          {Number(
            totals.igstAmount ||
              0
          ) > 0 && (
            <div className="quotation-view-total-row">
              <span>
                IGST
              </span>

              <strong>
                {formatCurrency(
                  totals.igstAmount,
                  tenantCurrency
                )}
              </strong>
            </div>
          )}

          <div className="quotation-view-total-row quotation-view-total-grand">
            <span>
              Grand Total
            </span>

            <strong>
              {formatCurrency(
                  grandTotal,
                  tenantCurrency
                )}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationViewPage;